import { Injectable } from '@angular/core';
import { Deserialize, Serialize } from 'cerialize';
import { CountryCode } from 'libphonenumber-js';
import moment from 'moment-timezone';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ServiceException } from '@uzurv/rideWss/app/exceptions';
import { DateUtil, isNullOrUndefined, PhoneNumberUtil } from '@uzurv/rideWss/app/util';
import { MobilityProfileDetail } from '@uzurv/rideWss/clara/models';
import { UzurvRestService } from '@uzurv/rideWss/http';
import {
  Address,
  CancellationReason,
  CollectionRequest,
  CreateRideSubscriptionRequest,
  DeviceInstallation,
  Driver,
  FavoriteLocation,
  FileData,
  Itinerary,
  Location,
  MiniDriver,
  OrganizationRider,
  OrganizationRiderListItem,
  OrganizationRiderServiceLevel,
  OrganizationServiceLevel,
  Payment,
  PaymentAccount,
  PositionUpdate,
  Provider,
  RegistrationRider,
  Release,
  Reservation,
  ReservationChargeDetail,
  RideEstimate,
  RideMetadataField,
  Rider,
  RiderDetail,
  RiderEligibilityProofRequest,
  RiderEligibilityProofResponse,
  RideRequest,
  RideRequestInfo,
  RideRequestType,
  RideReview,
  RiderNote,
  RideSubscriptionDetail,
  RuleValue,
  TransitProgram,
  TransitProgramInfo,
  TripDetail,
  TripListItem,
  User
} from '../../models';
import { ItinerarySanitizer, RideRequestSanitizer } from '../../sanitizers';

@Injectable({
  providedIn: 'root'
})
export class RiderService extends UzurvRestService {
  readonly resourceUrl = 'riders/';

  constructor(private itinerarySanitizer: ItinerarySanitizer, private rideRequestSanitizer: RideRequestSanitizer) {
    super();
  }

  /**
   * Proofs a rider's eligibility
   *
   * @param riderEligibilityProof
   * @permission anonymous
   */
  proofEligibility(riderEligibilityProof: RiderEligibilityProofRequest): Observable<RiderEligibilityProofResponse> {
    let countryCode: CountryCode = PhoneNumberUtil.getCountryCode(riderEligibilityProof.phoneNumber);

    if (isNullOrUndefined(countryCode)) {
      countryCode = 'US';
    }

    const hasValidPhoneNumber = PhoneNumberUtil.isValidPhoneNumber(riderEligibilityProof.phoneNumber, countryCode);

    if (!hasValidPhoneNumber) {
      return throwError(() => new ServiceException('Invalid Phone Number'));
    }

    riderEligibilityProof.phoneNumber = PhoneNumberUtil.formattedPhoneNumberForRest(riderEligibilityProof.phoneNumber, countryCode);

    return this.post<RiderEligibilityProofRequest>(`${this.resourceUrl}proofEligibility/`, Serialize(riderEligibilityProof)).pipe(
      map(response => Deserialize(response, RiderEligibilityProofResponse))
    );
  }

  /**
   * Registers an existing rider
   *
   * @param registrationRider
   * @permission rider
   */
  registerRider(registrationRider: RegistrationRider): Observable<User> {
    const payload: RegistrationRider = structuredClone(registrationRider);

    let countryCode: CountryCode = PhoneNumberUtil.getCountryCode(payload.user.person.phoneNumber);

    if (isNullOrUndefined(countryCode)) {
      countryCode = 'US';
    }

    const hasValidPhoneNumber = PhoneNumberUtil.isValidPhoneNumber(payload.user.person.phoneNumber, countryCode);

    if (!hasValidPhoneNumber) {
      return throwError(() => new ServiceException('Invalid Phone Number'));
    }

    payload.user.person.phoneNumber = PhoneNumberUtil.formattedPhoneNumberForRest(payload.user.person.phoneNumber, countryCode);

    return this.post<RegistrationRider>(this.resourceUrl, Serialize(payload)).pipe(map(response => Deserialize(response, User)));
  }

  /**
   * Returns a rider by username
   *
   * @param username
   */
  findRiderByUsername(username: string): Observable<RiderDetail> {
    return this.get<RiderDetail>(`${this.resourceUrl}`, {
      emailAddress: username
    }).pipe(map(response => Deserialize(response, RiderDetail)));
  }

  /**
   * Saves a rider profile picture.
   *
   * @param riderId
   * @param fileData
   */
  saveRiderProfilePicture(riderId: number, fileData: FileData): Observable<FileData> {
    return this.post<FileData>(`${this.resourceUrl}${riderId}/profilePicture`, Serialize(fileData)).pipe(
      map(response => Deserialize(response, FileData))
    );
  }

  /**
   * Get a riders image as FileData
   *
   * @param riderId
   */
  getRiderProfilePicture(riderId: number): Observable<FileData> {
    return this.get(`${this.resourceUrl}${riderId}/profilePicture`).pipe(map(response => Deserialize(response, FileData)));
  }

  /**
   * Search a rider by arbitrary means. Will match on name, username, ID, etc.
   *
   * @param searchString
   * @param pageNumber
   * @param pageSize
   * @permission rider_search
   */
  searchForRider(searchString: string, pageNumber = 0, pageSize = 7): Observable<Array<OrganizationRider>> {
    return this.get(`search/${this.resourceUrl}`, {
      searchString: searchString,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, OrganizationRider)));
  }

  /**
   * Returns a list of roles for a rider
   * TODO: Verify return type
   *
   * @param id
   */
  getRiderRolesById(id): Observable<any> {
    return this.get('users/' + id + '/roles/', {
      pageNumber: 0,
      pageSize: 100
    });
  }

  /**
   * Returns a list of rideRequests by rider ID
   *
   * @param riderId
   * @param pageNumber
   * @param pageSize
   */
  getRideRequestsByRiderId(riderId: number, pageNumber: number, pageSize: number): Observable<Array<RideRequest>> {
    const params = {
      pageNumber: pageNumber,
      pageSize: pageSize
    };

    return this.get<Array<RideRequest>>('riders/' + riderId + '/rideRequests/', params).pipe(
      map(response => Deserialize(response, RideRequest))
    );
  }

  /**
   * Get a drivers image as FileData
   *
   * @param riderId
   * @param reservationId
   */
  getReservationDriverProfilePicture(riderId: number, reservationId: number): Observable<FileData> {
    return this.get(`${this.resourceUrl}${riderId}/reservations/${reservationId}/driverProfilePicture`).pipe(
      map(response => Deserialize(response, FileData))
    );
  }

  /**
   * Get a drivers vehicle image as FileData
   *
   * @param riderId
   * @param reservationId
   */
  getReservationVehicleImage(riderId: number, reservationId: number): Observable<FileData> {
    return this.get(`${this.resourceUrl}${riderId}/reservations/${reservationId}/vehicleImage`).pipe(
      map(response => Deserialize(response, FileData))
    );
  }

  /**
   * Returns a list of favorite locations by rider ID
   *
   * @param riderId
   */
  listFavoriteLocationsByRiderId(riderId: number): Observable<Array<FavoriteLocation>> {
    return this.get<Array<Location>>('riders/' + riderId + '/favoriteLocations/').pipe(
      map(response => Deserialize(response, FavoriteLocation))
    );
  }

  /**
   * Returns a list of paymentAccounts by rider ID
   *
   * @param riderId
   * @param pageNumber
   * @param pageSize
   */
  listAllPaymentAccountsByRiderId(riderId: number, pageNumber = 0, pageSize = 100): Observable<Array<PaymentAccount>> {
    return this.get<Array<PaymentAccount>>(`riders/${riderId}/paymentAccounts/`, {
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, PaymentAccount)));
  }

  /**
   * Creates a new payment account with the provided object. Copying the
   * billing information solves a quick with Authorize.net where the address
   * isn't immediately available in the creation RO
   *
   * @param riderId
   * @param paymentAccount
   * @permission rider
   */
  createPaymentAccount(riderId: number, paymentAccount: PaymentAccount): Observable<PaymentAccount> {
    return this.post<PaymentAccount>(`${this.resourceUrl}${riderId}/paymentAccounts/`, Serialize(paymentAccount))
      .pipe(
        map(response => {
          let savedPaymentAccount: PaymentAccount;

          if (response) {
            savedPaymentAccount = Deserialize(response, PaymentAccount);
            savedPaymentAccount = this.copyBillingInformation(paymentAccount, savedPaymentAccount);
          }
          return Deserialize(savedPaymentAccount, PaymentAccount);
        })
      )
      .pipe(
        catchError(err => {
          const message = isNullOrUndefined(err.message) ? '' : err.message;

          // Map to translated errors
          if (message.includes('Preauth failed')) {
            err.message = 'Error: Preauth failed';
          }

          return throwError(() => err);
        })
      );
  }

  /**
   * Updates an existing payment account
   *
   * @param riderId
   * @param paymentAccount
   * @permission riderId
   */
  updatePaymentAccount(riderId: number, paymentAccount: PaymentAccount): Observable<PaymentAccount> {
    return this.put<PaymentAccount>(`${this.resourceUrl}${riderId}/paymentAccounts/${paymentAccount.id}`, Serialize(paymentAccount)).pipe(
      map(response => {
        let savedPaymentAccount: PaymentAccount;

        if (response) {
          savedPaymentAccount = Deserialize(response, PaymentAccount);
          savedPaymentAccount = this.copyBillingInformation(paymentAccount, savedPaymentAccount);
        }
        return Deserialize(savedPaymentAccount, PaymentAccount);
      })
    );
  }

  /**
   * Deletes a payment account from the system
   *
   * @param riderId
   * @param paymentAccountId
   * @permission riderId
   */
  deletePaymentAccount(riderId: number, paymentAccountId: number): Observable<boolean> {
    return this.delete(`${this.resourceUrl}${riderId}/paymentAccounts/${paymentAccountId}`);
  }

  /**
   * Adds a location to a riders favorite location list
   *
   * @param riderId
   * @param location
   * @param label
   * @permission rider
   */
  addFavoriteLocationForRiderWithLabel(riderId: number, location: Location, label: string): Observable<FavoriteLocation> {
    return this.post<Location>(`${this.resourceUrl}${riderId}/favoriteLocations/?label=${label}`, Serialize(location)).pipe(
      map(response => Deserialize(response, FavoriteLocation))
    );
  }

  /**
   * @param riderId
   * @param locationId
   * @permission rider
   */
  deleteFavoriteLocation(riderId: number, locationId: number) {
    return this.delete<boolean>(`${this.resourceUrl}${riderId}/favoriteLocations/${locationId}`);
  }

  /**
   * Returns a list of recent locations by rider ID
   *
   * @param riderId
   * @param pageNumber
   * @param pageSize
   */
  listRecentLocationsByRiderId(riderId: number, pageNumber: number, pageSize: number): Observable<Array<Location>> {
    const params = {
      pageNumber: pageNumber,
      pageSize: pageSize
    };
    return this.get<Array<Location>>('riders/' + riderId + '/recentLocations/', params).pipe(
      map(response => Deserialize(response, Location))
    );
  }

  /**
   * Returns a list of ignored drivers for a given rider by ID
   *
   * @param riderId
   */
  getIgnoredDriversByRiderId(riderId: number): Observable<Array<Driver>> {
    return this.get<Array<Driver>>(`${this.resourceUrl}${riderId}/ignoredDrivers/`).pipe(map(response => Deserialize(response, Driver)));
  }

  /**
   * Adds a driver to a riders ignored drivers list
   *
   * @param driver
   * @param rider
   * @permission rider
   */
  addIgnoredDriverToRider(driver: Driver, rider: Rider): Observable<Rider> {
    return this.post<Rider>(`${this.resourceUrl}${rider.id}/ignoredDrivers/`, Serialize(driver)).pipe(
      map(response => {
        if (!this.doesRiderHaveIgnoredDriver(rider, driver)) {
          rider.ignoredDrivers.push(driver);
        }
        return rider;
      })
    );
  }

  /**
   * Removes a driver from a riders ignored drivers list
   *
   * @param driver
   * @param rider
   * @permission rider
   */
  removeIgnoredDriverFromRider(driver: Driver, rider: Rider): Observable<Rider> {
    return this.delete<Rider>(`${this.resourceUrl}${rider.id}/ignoredDrivers/${driver.id}`).pipe(
      map(response => {
        rider.ignoredDrivers = rider.ignoredDrivers.filter(d => d.id !== driver.id);
        return rider;
      })
    );
  }

  /**
   * Tests whether a rider has ignored a driver and returns the driver if so
   *
   * @param rider
   * @param driver
   */
  doesRiderHaveIgnoredDriver(rider: Rider, driver: Driver): boolean {
    if (isNullOrUndefined(rider.ignoredDrivers)) {
      return false;
    }

    return !isNullOrUndefined(rider.ignoredDrivers.find(d => d.id === driver.id));
  }

  /**
   * Updates a rider with new information
   *
   * @param rider
   */
  patchRider(rider: Rider): Observable<RiderDetail> {
    const patchDictionary = {
      id: rider.id,
      firstName: rider.firstName,
      lastName: rider.lastName,
      phoneNumber: rider.phoneNumber,
      smsEnabledPhone: rider.smsEnabledPhone
    };

    return this.patch<RiderDetail>(`${this.resourceUrl}${rider.id}`, patchDictionary).pipe(
      map(response => Deserialize(response, RiderDetail))
    );
  }

  /**
   * Deletes a Rider's account.
   *
   * This action is irreversable!
   *
   * @param riderId
   * @permission rider
   */
  deleteAccount(riderId: number): Observable<boolean> {
    return this.delete<boolean>(`${this.resourceUrl}${riderId}`).pipe(map(() => true));
  }

  /**
   * Returns a list of tripListItems by rider ID
   *
   * @param riderId
   * @param pageNumber
   * @param pageSize
   * @param isActive
   */
  getTripListItemsByRiderId(riderId: number, pageNumber: number, pageSize: number, isActive: boolean): Observable<Array<TripListItem>> {
    const params = {
      pageNumber: pageNumber,
      pageSize: pageSize,
      isActive: isActive
    };

    return this.get<Array<TripListItem>>(`${this.resourceUrl}${riderId}/rideRequests/`, params).pipe(
      map(response => Deserialize(response, TripListItem))
    );
  }

  /**
   * Returns a TripDetail by riderId and rideRequestId
   *
   * @param riderId
   * @param rideRequestId
   */
  getTripDetailByRideRequestId(riderId: number, rideRequestId: number): Observable<TripDetail> {
    return this.get<TripDetail>(`${this.resourceUrl}${riderId}/rideRequests/${rideRequestId}`).pipe(
      map(response => Deserialize(response, TripDetail))
    );
  }

  /*
   * Activates a willCall ride
   * @param riderId
   * @param rideRequestId
   */
  activateWillCallRide(riderId: number, rideRequestId: number): Observable<TripDetail> {
    return this.put(`${this.resourceUrl}${riderId}/rideRequests/${rideRequestId}/activateWillCall`, {}).pipe(
      map(response => Deserialize(response, TripDetail))
    );
  }

  /**
   * PUTs the rider's location by ID, latitude, and longitude. Will default to
   * the current timestamp, or one can be supplied
   *
   * @param riderId
   * @param latitude
   * @param longitude
   * @param timestamp
   * @permission rider
   */
  updateRiderPosition(riderId: number, latitude: number, longitude: number, timestamp = new Date()): Observable<null> {
    const positionUpdate = new PositionUpdate();
    positionUpdate.personId = riderId;
    positionUpdate.latitude = latitude;
    positionUpdate.longitude = longitude;
    positionUpdate.timestamp = timestamp;

    return this.put(`${this.resourceUrl}${riderId}/riderPosition`, Serialize(positionUpdate));
  }

  /**
   * Gets the geocoordinate position of the driver attached to a reservation
   *
   * @param riderId
   * @param reservationId
   * @permission rider
   */
  getDriverPosition(riderId: number, reservationId: number): Observable<MiniDriver> {
    return this.get<MiniDriver>(`${this.resourceUrl}${riderId}/reservations/${reservationId}/driverPosition`).pipe(
      map(response => Deserialize(response, MiniDriver))
    );
  }

  /**
   * Creates a new itinerary
   *
   * @param riderId
   * @param itinerary
   * @permission rider
   */
  createItinerary(riderId: number, itinerary: Itinerary): Observable<Itinerary> {
    return this.post<Itinerary>(`${this.resourceUrl}${riderId}/itineraries/`, Serialize(this.itinerarySanitizer.sanitize(itinerary))).pipe(
      map(response => Deserialize(response, Itinerary))
    );
  }

  /**
   * Adds a ride request to an itinerary.
   *
   * @param riderId
   * @param itineraryId
   * @param rideRequest
   */
  addRideRequestToItinerary(riderId: number, itineraryId: number, rideRequest: RideRequest): Observable<RideRequest> {
    return this.post<RideRequest>(
      `${this.resourceUrl}${riderId}/itineraries/${itineraryId}/rideRequests/`,
      Serialize(this.rideRequestSanitizer.sanitize(rideRequest))
    ).pipe(map(response => Deserialize(response, RideRequest)));
  }

  /**
   * Patches a ride request
   *
   * @param riderId
   * @param rideRequestId
   * @param patchDictionary
   */
  patchRideRequest(riderId: number, rideRequestId: number, patchDictionary: Partial<RideRequest>): Observable<TripDetail> {
    patchDictionary.id = rideRequestId;

    return this.patch<TripDetail>(`${this.resourceUrl}${riderId}/rideRequests/${rideRequestId}`, Serialize(patchDictionary)).pipe(
      map(response => Deserialize(response, TripDetail))
    );
  }

  /**
   * Updates the rider note for a ride request
   *
   * @param riderId
   * @param rideRequestId
   * @param riderNote
   * @permission rider
   */
  putRiderNote(riderId: number, rideRequestId: number, riderNote: RiderNote): Observable<TripDetail> {
    return this.put<TripDetail>(`${this.resourceUrl}${riderId}/rideRequests/${rideRequestId}/riderNote`, Serialize(riderNote)).pipe(
      map(response => Deserialize(response, TripDetail))
    );
  }

  /**
   * Deletes the rider note for a ride request
   *
   * @param riderId
   * @param rideRequestId
   * @permission rider
   */
  deleteRiderNote(riderId: number, rideRequestId: number): Observable<TripDetail> {
    return this.delete<TripDetail>(`${this.resourceUrl}${riderId}/rideRequests/${rideRequestId}/riderNote`).pipe(
      map(response => Deserialize(response, TripDetail))
    );
  }

  /**
   * Delays the pick up time for ride back up
   *
   * @param riderId
   * @param rideRequestId
   */
  delayRideRequestPickUpTime(riderId: number, rideRequestId: number): Observable<TripDetail> {
    const patchDictionary: Partial<RideRequest> = {
      id: rideRequestId
    };

    return this.patch<TripDetail>(
      `${this.resourceUrl}${riderId}/rideRequests/${rideRequestId}/delayPickUpTime`,
      Serialize(patchDictionary)
    ).pipe(map(response => Deserialize(response, TripDetail)));
  }

  /**
   * Cancels a ride request in an itinerary
   *
   * @param riderId
   * @param rideRequestId
   * @param cancellationReasonId
   */
  cancelRideRequestWithCancellationReason(riderId: number, rideRequestId: number, cancellationReasonId: number): Observable<TripDetail> {
    const rideRequest: RideRequest = new RideRequest();
    rideRequest.id = rideRequestId;
    rideRequest.cancellationReasonId = cancellationReasonId;
    return this.patch<TripDetail>(`${this.resourceUrl}${riderId}/rideRequests/${rideRequestId}`, rideRequest).pipe(
      map(response => Deserialize(response, TripDetail))
    );
  }

  /**
   * Cancels a reservation in an itinerary
   *
   * @param riderId
   * @param reservationId
   * @param cancellationReason
   */
  cancelReservationWithCancellationReason(
    riderId: number,
    reservationId: number,
    cancellationReason: CancellationReason
  ): Observable<TripDetail> {
    const reservation: Reservation = new Reservation();
    reservation.id = reservationId;
    reservation.cancellationReason = cancellationReason;
    reservation.cancelled = true;
    return this.patch<TripDetail>(`${this.resourceUrl}${riderId}/reservations/${reservationId}`, reservation).pipe(
      map(response => Deserialize(response, TripDetail))
    );
  }

  /**
   * Creates a ride subscription
   *
   * @param riderId
   * @param createRideSubscriptionRequest
   * @permission rider
   */
  createRideSubscription(
    riderId: number,
    createRideSubscriptionRequest: CreateRideSubscriptionRequest
  ): Observable<RideSubscriptionDetail> {
    return this.post<Itinerary>(`${this.resourceUrl}${riderId}/rideSubscriptions`, Serialize(createRideSubscriptionRequest)).pipe(
      map(response => Deserialize(response, RideSubscriptionDetail))
    );
  }

  /**
   * Generate phone call info
   *
   * @param riderId
   * @param reservationId
   */
  generatePhoneCallInfo(riderId: number, reservationId: number): Observable<string> {
    return this.post<void>(`${this.resourceUrl}${riderId}/reservations/${reservationId}/phoneCalls/`, {});
  }

  /**
   * Gets ride estimates by ride request id
   *
   * @param riderId
   * @param rideRequestId
   */

  getRideEstimatesByRideRequestId(riderId: number, rideRequestId: number): Observable<RideEstimate> {
    return this.get<void>(`${this.resourceUrl}${riderId}/rideRequests/${rideRequestId}/rideEstimates/`, {}).pipe(
      map(response => Deserialize(response, RideEstimate))
    );
  }

  /**
   * Returns a list of cancellation reasons, optionally overriding the cached values
   *
   * @param overrideCache
   */
  getCancellationReasons(): Observable<Array<CancellationReason>> {
    return this.get<Array<CancellationReason>>(`${this.resourceUrl}cancellationReasons/`).pipe(
      map(response => Deserialize(response, CancellationReason))
    );
  }

  /**
   * Returns a cancellation reason by ID
   *
   * @param id
   * @param overrideCache
   */
  findCancellationReasonWithId(id: number): Observable<CancellationReason> {
    return this.getCancellationReasons().pipe(map(response => response.find(cancellationReason => cancellationReason.id === id)));
  }

  /**
   * Returns a cancellation reason by name
   *
   * @param name
   * @param overrideCache
   */
  findCancellationReasonWithName(name: string): Observable<CancellationReason> {
    return this.getCancellationReasons().pipe(map(response => response.find(cancellationReason => cancellationReason.name === name)));
  }

  /**
   * Returns a list of organizationServiceLevels
   *
   * @param organizationId
   *
   */
  getOrganizationServiceLevels(organizationId: number): Observable<Array<OrganizationServiceLevel>> {
    return this.get(`${this.resourceUrl}organizations/${organizationId}/serviceLevels/`).pipe(
      map(response => Deserialize(response, OrganizationServiceLevel))
    );
  }

  listAvailableProviders(
    riderId: number,
    transitProgramId: number,
    pickupDate: Date,
    dropoffDate: Date,
    serviceLevelCode: string
  ): Observable<Array<Provider>> {
    if (isNullOrUndefined(riderId)) {
      throw new ServiceException('Missing Rider Id');
    }

    if (isNullOrUndefined(transitProgramId)) {
      throw new ServiceException('Missing Transit Program Id');
    }

    if (isNullOrUndefined(pickupDate)) {
      throw new ServiceException('Missing Pick Up Date');
    }

    if (isNullOrUndefined(dropoffDate)) {
      throw new ServiceException('Missing Drop Off Date');
    }

    if (isNullOrUndefined(serviceLevelCode)) {
      throw new ServiceException('Missing Service Level Code');
    }

    return this.get(`${this.resourceUrl}${riderId}/availableProviders/`, {
      transitProgramId: transitProgramId,
      pickupDate: moment(pickupDate).format('YYYY-MM-DD HH:mm:ss'),
      dropoffDate: moment(dropoffDate).format('YYYY-MM-DD HH:mm:ss'),
      serviceLevelCode: serviceLevelCode
    }).pipe(map(response => (isNullOrUndefined(response) ? [] : Deserialize(response, Provider))));
  }

  /**
   * Returns a list of organizationRiderServiceLevels
   *
   * @param riderId
   * @param organizationRiderId
   *
   */
  getOrganizationRiderServiceLevels(riderId: number, organizationRiderId: number): Observable<Array<OrganizationRiderServiceLevel>> {
    return this.get(`${this.resourceUrl}${riderId}/organizationRiders/${organizationRiderId}/serviceLevels/`).pipe(
      map(response => Deserialize(response, OrganizationRiderServiceLevel))
    );
  }

  /**
   * Adds an organizationRiderServiceLevel
   *
   * @param riderId
   * @param organizationRiderId
   * @param organizationRiderServiceLevel
   */
  addOrganizationRiderServiceLevel(
    riderId: number,
    organizationRiderId,
    organizationRiderServiceLevel: OrganizationRiderServiceLevel
  ): Observable<OrganizationRiderServiceLevel> {
    return this.post<OrganizationRiderServiceLevel>(
      `${this.resourceUrl}${riderId}/organizationRiders/${organizationRiderId}/serviceLevels/`,
      Serialize(organizationRiderServiceLevel)
    ).pipe(map(response => Deserialize(response, OrganizationRiderServiceLevel)));
  }

  /**
   * Disables an organizationRiderServiceLevel
   *
   * @param riderId
   * @param organizationRiderId
   * @param serviceLevelId
   */
  disableOrganizationRiderServiceLevel(riderId: number, organizationRiderId: number, serviceLevelId: number): Observable<boolean> {
    return this.delete(`${this.resourceUrl}${riderId}/organizationRiders/${organizationRiderId}/serviceLevels/${serviceLevelId}`);
  }

  /**
   * Returns a list of rule values
   *
   */
  getRuleValues(): Observable<Array<RuleValue>> {
    return this.get(`${this.resourceUrl}ruleValues/`, { pageNumber: 0, pageSize: 1000 }).pipe(
      map(response => Deserialize(response, RuleValue))
    );
  }

  /**
   * Returns a rule value by ID
   *
   * @param id
   */
  findRuleValueWithId(id: number): Observable<RuleValue> {
    return this.getRuleValues().pipe(map(response => response.find(ruleValue => ruleValue.id === id)));
  }

  /**
   * Returns a rule value by name
   *
   * @param name
   */
  findRuleValueWithName(name: string): Observable<RuleValue> {
    return this.getRuleValues().pipe(map(response => response.find(ruleValue => ruleValue.name === name)));
  }

  /**
   * Returns a list of ride request types
   */
  listRideRequestTypes(): Observable<Array<RideRequestType>> {
    return this.get<Array<RideRequestType>>(`${this.resourceUrl}rideRequestTypes/`).pipe(
      map(response => Deserialize(response, RideRequestType))
    );
  }

  /**
   * Finds the nearest transit program in the supplied organization to the supplied location
   *
   * @param organizationId
   * @param pickUpLocationId
   * @param dropOffLocationId
   * @permission organization_read
   */
  findNearestTransitProgramByLocations(
    organizationId: number,
    pickUpLocationId: number,
    dropOffLocationId
  ): Observable<TransitProgramInfo> {
    return this.get<TransitProgram>(`${this.resourceUrl}organizations/${organizationId}/transitPrograms/`, {
      pickUpLocationId: pickUpLocationId,
      dropOffLocationId: dropOffLocationId
    }).pipe(map(response => Deserialize(response, TransitProgramInfo)));
  }

  /**
   * Gets a list of OrganizationRiders based on riderId
   *
   * @param riderId
   */
  getOrganizationRidersByRiderId(riderId: number): Observable<Array<OrganizationRiderListItem>> {
    return this.get<Array<OrganizationRiderListItem>>(`${this.resourceUrl}${riderId}/organizations/`).pipe(
      map(response => Deserialize(response, OrganizationRiderListItem))
    );
  }

  /**
   * Asks the server to resend email verification
   *
   * @param emailAddress
   */
  resendVerifyEmail(emailAddress: string): Observable<boolean> {
    return this.get(`${this.resourceUrl}verifyEmail/requestVerificationToken`, {
      emailAddress: emailAddress
    });
  }

  /**
   * Requests the server send a verification email to the updated email address.
   *
   * @param rider
   */
  patchContactInformation(rider: Rider): Observable<RiderDetail> {
    return this.patch(`${this.resourceUrl}${rider.id}/contactInformation`, rider).pipe(map(response => Deserialize(response, RiderDetail)));
  }

  getTransitProgramRideRequestInfo(
    transitProgramId: number,
    pickupLatitude: number,
    pickupLongitude: number,
    dropoffLatitude: number,
    dropoffLongitude: number,
    billablePassengerCount: number,
    pickupDate: Date,
    dropoffDate: Date,
    serviceLevelId: number
  ): Observable<RideRequestInfo> {
    const params = {
      transitProgramId: transitProgramId,
      pickupLatitude: pickupLatitude,
      pickupLongitude: pickupLongitude,
      dropoffLatitude: dropoffLatitude,
      dropoffLongitude: dropoffLongitude,
      billablePassengerCount: billablePassengerCount,
      serviceLevelId: serviceLevelId
    };

    if (!isNullOrUndefined(pickupDate)) {
      params['pickupDate'] = DateUtil.getFormattedDateInUtc(pickupDate, 'YYYY-MM-DD HH:mm:ss');
    }

    if (!isNullOrUndefined(dropoffDate)) {
      params['dropoffDate'] = DateUtil.getFormattedDateInUtc(dropoffDate, 'YYYY-MM-DD HH:mm:ss');
    }

    return this.get<RideRequestInfo>(`${this.resourceUrl}rideRequestInfo`, params).pipe(
      map(response => Deserialize(response, RideRequestInfo))
    );
  }

  /**
   * Used to create or look up a location. The backend will return an existing location (if it exists)
   * or create a new one and return it with the fields filled out.
   *
   * @param display The display address of a location, e.g. from Google Maps Autocomplete
   * @permission location_create
   */
  addLocation(display: string): Observable<Location> {
    const location = new Location();
    location.display = display;
    return this.post<Location>(`${this.resourceUrl}locations`, Serialize(location)).pipe(map(response => Deserialize(response, Location)));
  }

  /**
   * Returns a list of charges by organizationrider ID
   *
   * @param organizationRiderId
   * @param unpaid
   * @permission organization_rider_read
   */
  listAllCharges(organizationRiderId: number, unpaid: boolean): Observable<Array<ReservationChargeDetail>> {
    return this.get(`${this.resourceUrl}${organizationRiderId}/charges/`, {
      unpaidOnly: unpaid,
      pageSize: 1000,
      pageNumber: 0
    }).pipe(map(response => Deserialize(response, ReservationChargeDetail)));
  }

  /**
   * Collects payment for an organization rider
   *
   * @param riderId
   * @param charges
   * @permission payment_processing
   */
  collectPaymentForCharges(riderId: number, charges: CollectionRequest): Observable<Payment> {
    return this.post<Payment>(`${this.resourceUrl}${riderId}/charges/collect`, charges).pipe(
      map(response => Deserialize(response, Payment))
    );
  }

  /**
   * Copies values from one payment account to another
   *
   * @param from
   * @param to
   */
  private copyBillingInformation(from: PaymentAccount, to: PaymentAccount): PaymentAccount {
    if (from.billingStreetAddress1) {
      to.billingStreetAddress1 = from.billingStreetAddress1;
    }

    if (from.billingStreetAddress2) {
      to.billingStreetAddress2 = from.billingStreetAddress2;
    }

    if (from.billingState) {
      to.billingState = from.billingState;
    }

    if (from.billingCity) {
      to.billingCity = from.billingCity;
    }

    if (from.billingZipCode) {
      to.billingZipCode = from.billingZipCode;
    }

    return to;
  }

  /**
   * Gets a rider's current address
   *
   * @param riderId
   */
  findAddressByRiderId(riderId: number): Observable<Address> {
    return this.get<Address>(`${this.resourceUrl}${riderId}/address`).pipe(map(response => Deserialize(response, Address)));
  }

  /**
   * Adds a rider address
   *
   * @param riderId
   * @param address
   * @permission rider
   */
  addAddress(riderId: number, address: Address): Observable<Address> {
    return this.post<Address>(`${this.resourceUrl}addresses/`, Serialize(address)).pipe(map(response => Deserialize(response, Address)));
  }

  /**
   * @param id
   * @param transitProgramId
   * @permission rider
   */
  listRideMetadataFieldsForTransitProgram(id: number, transitProgramId: number): Observable<Array<RideMetadataField>> {
    return this.get<Array<RideMetadataField>>(`${this.resourceUrl}${id}/transitPrograms/${transitProgramId}/rideMetadataFields/`).pipe(
      map(response => Deserialize(response, RideMetadataField))
    );
  }

  /**
   * Returns any matching unclaimed organization riders for the rider id.
   *
   * @param riderId
   * @returns
   */
  listMatchingUnclaimedOrganizationRiders(riderId: number): Observable<Array<OrganizationRiderListItem>> {
    return this.get<Array<OrganizationRiderListItem>>(`${this.resourceUrl}${riderId}/matchingUnclaimedOrganizationRiders/`).pipe(
      map(response => Deserialize(response, OrganizationRiderListItem))
    );
  }

  claimOrganizationRider(
    riderId: number,
    organizationRiderId: number,
    riderEligibilityProofRequest: RiderEligibilityProofRequest
  ): Observable<OrganizationRiderListItem> {
    return this.put<RiderEligibilityProofRequest>(
      `${this.resourceUrl}${riderId}/organizationRiders/${organizationRiderId}/claim`,
      Serialize(riderEligibilityProofRequest)
    ).pipe(map(response => Deserialize(response, OrganizationRiderListItem)));
  }

  /**
   * Removes the unclaimed organization rider entry for the matching rider id & organization rider id.
   *
   * @param riderId
   * @param organizationRiderId
   * @returns
   */
  ignoreOrganizationRider(riderId: number, organizationRiderId: number): Observable<OrganizationRiderListItem> {
    return this.delete<OrganizationRiderListItem>(`${this.resourceUrl}${riderId}/organizationRiders/${organizationRiderId}/`).pipe(
      map(response => Deserialize(response, OrganizationRiderListItem))
    );
  }

  /**
   * Submits a driver rating left by the user.
   *
   * @param riderId
   * @param reservationId
   * @param driverRating
   * @returns
   */
  submitDriverRating(riderId: number, reservationId: number, driverRating: RideReview): Observable<RideReview> {
    return this.post<RideReview>(`${this.resourceUrl}${riderId}/reservations/${reservationId}/rideReviews/`, Serialize(driverRating)).pipe(
      map(response => Deserialize(response, RideReview))
    );
  }

  /************************************* Releases ***********************/

  findReleaseByAppBundleIdentifierReleasePlatformCodeAndVersion(
    riderId: number,
    appBundleIdentifier: string,
    releasePlatformCode: string,
    version: string
  ): Observable<Release> {
    return this.get<Array<Release>>(`${this.resourceUrl}${riderId}/releases/`, {
      appBundleIdentifier: appBundleIdentifier,
      releasePlatformCode: releasePlatformCode,
      version: version
    }).pipe(map(response => Deserialize(response, Release)));
  }

  /************************************* Device Installations ***********************/

  /**
   * Returns a list of device installations for a user
   *
   * @param riderId
   */
  getDeviceInstallations(riderId: number): Observable<Array<DeviceInstallation>> {
    return this.get<Array<DeviceInstallation>>(`riders/${riderId}/deviceInstallations/`).pipe(
      map(response => Deserialize(response, DeviceInstallation))
    );
  }

  /**
   * Creates a new device installation for a user
   *
   * @param riderId
   * @param deviceInstallation
   */
  createDeviceInstallation(riderId: number, deviceInstallation: DeviceInstallation): Observable<DeviceInstallation> {
    return this.post<DeviceInstallation>(`riders/${riderId}/deviceInstallations/`, Serialize(deviceInstallation)).pipe(
      map(response => Deserialize(response, DeviceInstallation))
    );
  }

  /**
   * Updates an existing device installation
   *
   * @param riderId
   * @param deviceInstallation
   */
  updateDeviceInstallation(riderId: number, deviceInstallation: DeviceInstallation): Observable<DeviceInstallation> {
    return this.put<DeviceInstallation>(
      `riders/${riderId}/deviceInstallations/${deviceInstallation.id}`,
      Serialize(deviceInstallation)
    ).pipe(map(response => Deserialize(response, DeviceInstallation)));
  }

  /**
   * Gets Rider Mobility Profile Detail
   *
   * @param riderId
   */
  getRiderMobilityProfileDetail(riderId: number): Observable<MobilityProfileDetail> {
    return this.get<MobilityProfileDetail>(`riders/${riderId}/mobilityProfile`).pipe(
      map(response => Deserialize(response, MobilityProfileDetail))
    );
  }

  /**
   * Updates Rider Mobility Profile Detail
   *
   * @param riderId
   * @param mobilityProfileDetail
   */
  updateRiderMobilityProfileDetail(riderId: number, mobilityProfileDetail: MobilityProfileDetail): Observable<MobilityProfileDetail> {
    return this.put<MobilityProfileDetail>(`riders/${riderId}/mobilityProfile`, Serialize(mobilityProfileDetail)).pipe(
      map(response => Deserialize(response, MobilityProfileDetail))
    );
  }

  /**
   * Verifies Rider Mobility Profile Detail
   *
   * @param riderId
   * @param mobilityProfileDetail
   */
  verifyRiderMobilityProfileDetail(riderId: number, mobilityProfileDetail: MobilityProfileDetail): Observable<MobilityProfileDetail> {
    return this.put<MobilityProfileDetail>(`riders/${riderId}/mobilityProfile/verify`, Serialize(mobilityProfileDetail)).pipe(
      map(response => Deserialize(response, MobilityProfileDetail))
    );
  }
}
